/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, September 26th 2022, 1:55:35 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/**Drag */
import immutableUpdateFn from "immutability-helper";
/**Drag */
import { PrimaryKeyValue, ObjectKey, ObjectValue } from "lib/types";

/**
 * addOrUpdateItem function adds an object to a list if it is not in that list else if updates the object
 * that is in the list.
 * The function compares objects based on the primary Key provided.
 * This function is only intended to work for elements that are saved and have unique ids.
 * New items not yet saved should not be put to state using this function but rather have an itemCreation Queue
 * tracking the new objects with separate logic.
 * @param list The list of objects
 * @param itemToUpdate The object to update
 * @param primaryKey The primary key that is a key of the objects
 * @param positionLabel The key that represents the position of the object in a sequential list other than a list index
 * @returns
 */

type TAddOperation = "append" | "prepend";
type TAddItemOptions = {
  addOperation: TAddOperation;
};

export const addOrUpdateItem = <T>(
  list: T[],
  itemToUpdate: T,
  primaryKey: ObjectKey<T>,
  positionLabel: ObjectKey<T> = null,
  addOptions?: TAddItemOptions
): T[] => {
  if (!itemToUpdate?.[primaryKey]) return list;

  const itemIsInList = checkItemIsInList(itemToUpdate, list, primaryKey);

  return itemIsInList
    ? replaceItemInList(itemToUpdate, list, primaryKey, positionLabel)
    : appendItemToList(itemToUpdate, list, positionLabel, primaryKey, addOptions);
};

export const removeItem = <T>(list: T[], itemId: PrimaryKeyValue<T>, primaryKey: keyof T): T[] => {
  return list.filter((item) => item[primaryKey] !== itemId);
};

export const checkItemIsInList = <T>(item: T, items: T[], uniqueIdentifier: ObjectKey<T>): boolean => {
  return items.some((tt) => tt[uniqueIdentifier] === item[uniqueIdentifier]);
};
export const appendItemToList = <T>(
  itemToAppend: T,
  items: T[],
  positionLabel: ObjectKey<T>,
  uniqueIdentifier: ObjectKey<T>,
  addOptions?: TAddItemOptions
) => {
  let updatedItems = items.map<T>((tt) => ({
    ...tt,
    [positionLabel]:
      tt[positionLabel] > itemToAppend[positionLabel]
        ? (tt[positionLabel] as unknown as number) + 1
        : tt[positionLabel],
  }));

  const addOperation = addOptions?.addOperation || "append";

  if (addOperation === "append") {
    updatedItems = updatedItems.concat(itemToAppend);
  }

  if (addOperation === "prepend") {
    updatedItems = [itemToAppend].concat(updatedItems);
  }

  return updatedItems;
};

export const replaceItemInList = <T>(
  updatedItem: T,
  items: T[],
  uniqueIdentifier: ObjectKey<T>,
  positionLabel: ObjectKey<T>
) => {
  const updatedItemWithCurrentPosition = { ...updatedItem }; // Making sure this is a pure function

  return items.map<T>((tt) => {
    if (tt[uniqueIdentifier] === updatedItem[uniqueIdentifier]) {
      updatedItemWithCurrentPosition[positionLabel] = tt[positionLabel]; // Making sure the updated item has the current position
      return updatedItemWithCurrentPosition;
    }

    return tt;
  });
};
export const getItemsWithNewPositions = <T>(
  items: T[],
  dragSourcePosition: ObjectValue<T> & number,
  hoverPosition: ObjectValue<T> & number,
  positionLabel: ObjectKey<T>,
  uniqueIdentifier: ObjectKey<T>
) => {
  let newItems = [...items];
  let affectedItems: T[] = [];
  let positionShift = 0;

  /** This has to be a pure function. */
  if (dragSourcePosition > hoverPosition) {
    // Dragging upwards

    affectedItems = items.filter(
      (item) => item[positionLabel] <= dragSourcePosition && item[positionLabel] >= hoverPosition
    );
    positionShift = 1;
  }

  if (dragSourcePosition < hoverPosition) {
    // Dragging downwards

    affectedItems = items.filter(
      (item) => item[positionLabel] >= dragSourcePosition && item[positionLabel] <= hoverPosition
    );
    positionShift = -1;
  }

  affectedItems.forEach((tt) => {
    if (tt[positionLabel] === dragSourcePosition) {
      newItems = immutableUpdateItemPositionInList(
        newItems,
        tt[uniqueIdentifier],
        hoverPosition,
        positionLabel,
        uniqueIdentifier
      );
      return;
    }

    newItems = immutableUpdateItemPositionInList(
      newItems,
      tt[uniqueIdentifier],
      (tt[positionLabel] as unknown as number) + positionShift,
      positionLabel,
      uniqueIdentifier
    );
    return;
  });

  return newItems;
};
export const immutableShift = <T>(items: T[]) => {
  const newItems = [...items];
  newItems.shift();
  return newItems;
};

const immutableUpdateItemPositionInList = <T>(
  originalList: T[],
  itemId: PrimaryKeyValue<T>,
  newPosition: ObjectValue<T> & number,
  positionLabel: ObjectKey<T>,
  uniqueIdentifier: ObjectKey<T>
) => {
  const targetIndex = originalList.findIndex((el) => el[uniqueIdentifier] === itemId);
  return immutableUpdateFn<T[], any>(originalList, {
    [targetIndex]: { [positionLabel]: { $set: newPosition } },
  });
};

// eslint-disable-next-line
const immutableMoveItems = (items, dragId, oldHoverItemPosition, positionLabel) => {
  // TODO: this functions is less expensive than getItemsWithNewPositions but it requires the drag to limit the
  // dragged element within the plane of the drop targets. i.e. restrict user to drag along the y axis of the list
  // and not outside. So consider using this if getItemsWithNewPositions performance becomes unbearable and
  // improve on it to provide more accuracy.
  const dragIndex = items.findIndex((el) => el.id === dragId);
  const hoverIndex = items.findIndex((el) => el[positionLabel] === oldHoverItemPosition);
  const oldDragSourcePosition = items[dragIndex]?.[positionLabel];

  // returning a list with the two positions interchanged
  return immutableUpdateFn(items, {
    [dragIndex]: { [positionLabel]: { $set: oldHoverItemPosition } },
    [hoverIndex]: { [positionLabel]: { $set: oldDragSourcePosition } },
  });
};
